import { Fragment, useState } from 'react'
import PropTypes from 'prop-types'
import axios from 'axios'
import to from 'await-to-js'
import { useTheme } from '@emotion/react'
import { Formik, Form } from 'formik'
import { Alert, Button, SectionTitle } from 'chaoskit/src/components'
import FormikField from 'chaoskit/src/components/FormikField'
import { misc } from 'chaoskit/src/assets/styles/utility'

import ButtonLink from './ButtonLink'
import BotField from './BotField'

import track from '~helpers/track'
import { freeSignupSchema } from '~utils/schemas/signup'

const SubmitButton = ({ isSubmitting, ...rest }) => (
  <Button loading={isSubmitting} actionType="submit" type="primary" {...rest}>
    Sign Up
  </Button>
)

SubmitButton.propTypes = {
  isSubmitting: PropTypes.bool,
}

const FreeSignUp = ({
  useGenericEmail,
  title = 'Sign up for free',
  sub,
  inline,
  onSuccess = () => {},
}) => {
  const theme = useTheme()

  const [response, setResponse] = useState()
  const [errorMessage, setErrorMessage] = useState(
    'There was an issue submitting your request. Please try again later.'
  )

  const emailLabel = useGenericEmail ? 'Email' : 'Work email'

  const getCurrentUrl = () =>
    typeof window !== 'undefined' ? window.location.href : ''

  const getInitialValues = () => {
    const initialValues = {
      'bot-field': '',
      admin_email: '',
      first_name: '',
      last_name: '',
      requested_demo: false,
      source_url: getCurrentUrl(),
    }

    return initialValues
  }

  const handleSubmit = async (values, { setSubmitting }) => {
    setResponse(null)
    setSubmitting(true)

    const [error, success] = await to(
      axios({
        method: 'POST',
        data: values,
        url: 'https://api.gremlin.com/v1/companies/invites/free',
      })
    )

    setSubmitting(false)

    if (success) {
      setResponse('success')

      track('Account created')

      if (window.analytics) {
        window.analytics.identify(values.admin_email, {
          email: values.admin_email,
          firstName: values.first_name,
          lastName: values.last_name,
          requestedDemo: values.requested_demo,
          sourceUrl: values.source_url,
        })
      }

      onSuccess()
    } else {
      if (error?.response?.data) {
        setErrorMessage(error?.response?.data)
      }

      setResponse('error')
    }
  }

  return (
    <Fragment>
      {title && (
        <SectionTitle
          align={{ medium: 'left' }}
          title={title}
          sub={sub}
          css={{ marginBottom: `${theme.space.large}px !important` }}
        />
      )}
      <Formik
        initialValues={getInitialValues()}
        validationSchema={freeSignupSchema}
        onSubmit={handleSubmit}
      >
        {({ values, isSubmitting }) => (
          <Fragment>
            {response === 'error' && (
              <Alert type="danger" title="Error">
                <p>{errorMessage}</p>
              </Alert>
            )}
            {response === 'success' && (
              <Alert type="primary" title="Success!">
                <p>Activation link sent to {values.admin_email}</p>
              </Alert>
            )}
            {!response && (
              <Form>
                <BotField name="bot-field" css={misc.hide} type="text" />
                <div
                  css={[
                    {
                      display: 'grid',
                      gap: theme.space.base,
                    },

                    inline && {
                      [theme.mq.medium]: {
                        gridTemplateColumns: 'repeat(2, 1fr)',
                      },
                    },
                  ]}
                >
                  <div>
                    <FormikField
                      required
                      name="first_name"
                      as="input"
                      label="First name"
                    />
                  </div>
                  <div>
                    <FormikField
                      required
                      name="last_name"
                      as="input"
                      label="Last name"
                    />
                  </div>
                  <div>
                    <FormikField
                      required
                      name="admin_email"
                      as="input"
                      type="email"
                      label={emailLabel}
                    />
                  </div>

                  {inline && (
                    <div
                      css={[
                        inline && {
                          [theme.mq.small__max]: {
                            textAlign: 'center',
                          },
                        },
                      ]}
                    >
                      <div
                        css={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(2, 1fr)',
                          gap: theme.space.base,
                        }}
                      >
                        <SubmitButton isSubmitting={isSubmitting} />
                        <ButtonLink
                          type="default"
                          url="https://app.gremlin.com"
                        >
                          Log in
                        </ButtonLink>
                      </div>
                    </div>
                  )}
                </div>

                {!inline && (
                  <div
                    css={{
                      marginTop: theme.space.large,

                      [theme.mq.small__max]: {
                        textAlign: 'center',
                      },
                    }}
                  >
                    <SubmitButton isSubmitting={isSubmitting} />
                  </div>
                )}
              </Form>
            )}
          </Fragment>
        )}
      </Formik>
    </Fragment>
  )
}

FreeSignUp.propTypes = {
  useGenericEmail: PropTypes.bool,
  title: PropTypes.string,
  onSuccess: PropTypes.func,
  sub: PropTypes.string,
  inline: PropTypes.bool,
}

export default FreeSignUp
